'use client'

import type { BigIntish, Fetchable } from '@morpho-org/blue-sdk'
import type { Options } from 'highcharts'

import { formatters } from '@morpho-org/ui-v6/utils'
import { memo, useMemo } from 'react'
import { formatUnits } from 'viem'

import type { ChartBaseProps } from '../types/chart'

import { createTooltipFormatter, mergeOptions } from '../helpers/highcharts'
import { useChartTheme } from '../hooks/useChartTheme'
import { useHighchartsOptions } from '../hooks/useHighchartsOptions'
import { Chart } from './Chart'

export interface MarketCollateralAtRiskChartProps extends Omit<ChartBaseProps, 'series'> {
  data: CollateralAtRiskData[]
  collateralAsset?: Fetchable<CollateralAsset>
}

export interface CollateralAsset {
  symbol: string
  decimals: number
}

export interface CollateralAtRiskData {
  collateralAssets: BigIntish
  collateralPriceRatio: number
  collateralUsd: number | null
}

export function CollateralAtRiskChart({
  data,
  unit,
  collateralAsset,
  ...props
}: MarketCollateralAtRiskChartProps) {
  const { theme, themeMode } = useChartTheme()
  const { options: baseOptions, palette } = useHighchartsOptions()

  const seriesData = useMemo(() => {
    if (!collateralAsset) {
      return []
    }
    switch (unit) {
      case collateralAsset.symbol:
        return data?.map(
          point =>
            // oxlint-disable-next-line typescript/consistent-type-assertions
            [
              point.collateralPriceRatio - 1,
              Number(formatUnits(BigInt(point.collateralAssets), collateralAsset?.decimals))
            ] as [number, number | null]
        )
      case '$':
        return data?.map(
          point =>
            // oxlint-disable-next-line typescript/consistent-type-assertions
            [point.collateralPriceRatio - 1, point.collateralUsd] as [number, number | null]
        )
    }
  }, [unit, collateralAsset, data])

  const options: Options = useMemo(() => {
    return mergeOptions(baseOptions, {
      chart: {
        plotBackgroundColor: 'var(--muted)'
      },
      plotOptions: {
        series: {
          lineWidth: 1,
          states: {
            hover: {
              lineWidthPlus: 0,
              halo: { size: 0 }
            }
          }
        },
        area: {
          lineWidth: 1
        }
      },
      xAxis: {
        type: 'linear',
        tickColor: 'var(--border)',
        tickLength: 5,
        tickPosition: 'outside',
        title: {
          text: 'Oracle price change'
        },
        labels: {
          formatter: function () {
            return formatters.percent.digits(2).unit('%').of(Number(this.value))
          }
        }
      },
      yAxis: {
        gridLineColor: 'var(--border)',
        opposite: false,
        showFirstLabel: false,
        showLastLabel: false,
        labels: {
          align: 'left',
          x: 10,
          style: {
            textAlign: 'left'
          },
          formatter: function () {
            return formatters.short
              .unit(unit)
              .digits(0)
              .default('-')
              .trailingZero(false)
              .of(Number(this.value))
          }
        },
        title: {
          text: 'Collateral at risk'
        }
      },
      tooltip: {
        formatter: createTooltipFormatter({
          formatters: {
            x: point => `${formatters.percent.digits(2).unit('%').of(point.x)} Oracle price change`,
            y: point => (point.y ? formatters.short.digits(2).unit(unit).of(point.y) : '-')
          },
          theme,
          themeMode
        })
      },
      series: [
        {
          type: 'area',
          name: 'Collateral at risk',
          data: seriesData,
          color: palette.colors[0],
          lineColor: palette.colors[0],
          fillColor: palette.areaChartColors[0]
        }
      ]
    })
  }, [seriesData, baseOptions, palette, theme, themeMode, unit])

  return <Chart options={options} {...props} />
}

export default memo(CollateralAtRiskChart)
